/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ActionTree, GetterTree, MutationTree, Module } from 'vuex'
import JWTUtils from '@/utils/JWTUtils'
import {
  AgreementApplyRequest,
  ContractReportResponse,
  SpecificAccountCodeOptionResponse
} from '@/types/api'
import { RootState } from '@/store'
import AgreementService from '@/services/AgreementService'
import OnlineService from '@/services/OnlineService'

// ts
export type AgreementState = {
  report: AgreementReport;
  confirm: AgreementConfirm;
  specificAccountCodes?: SpecificAccountCodeType | null;
  other: AgreementOther;
};

export type SpecificAccountCodeType = Record<
  'SPECIFIC_WITHHOLDING' | 'SPECIFIC,' | 'NORMAL',
  SpecificAccountCodeOptionResponse
>;

export type AgreementReport = {
  contract: Partial<ContractReportResponse>;
  preContract: Partial<ContractReportResponse>;
};

export type AgreementConfirm = {
  contract: boolean;
  preContract: boolean;
  other: boolean;
};

export type AgreementOther = {
  specificAccountCode: number | null;
  confirmSpecific: boolean;
};

const initialConfirm = {
  contract: false,
  preContract: false,
  other: false
}

const state: AgreementState = {
  report: {
    contract: {},
    preContract: {}
  },
  confirm: {
    contract: false,
    preContract: false,
    other: false
  },
  specificAccountCodes: null,
  other: {
    specificAccountCode: null,
    confirmSpecific: false
  }
}

const getters: GetterTree<AgreementState, RootState> = {
  /**
   * 申込可能な状態か
   * @param state
   */
  canApply (state): boolean {
    // 各画面チェック済み
    const allChecked = Object.keys(state.confirm).every(k => !!state.confirm[k as keyof AgreementConfirm])
    // その他契約等で、口座区分の選択が正しいこと（特定口座源泉徴収ありは、ウェルスナビに委任するチェックが必要）
    const validSpecificConfirm = state.other.specificAccountCode !== null &&
      (state.other.specificAccountCode !== state.specificAccountCodes!.SPECIFIC_WITHHOLDING.value || state.other.confirmSpecific)
    return allChecked && validSpecificConfirm
  }
}

const actions: ActionTree<AgreementState, RootState> = {
  // 約款取得
  getContract ({ state, commit, rootState }): void {
    if (Object.keys(state.report.contract).length) {
      return
    }
    const companyId = JWTUtils.getSubject(rootState.auth.jwt as string).companyId
    AgreementService.contract(companyId).then(data => {
      commit('contract', data)
    })
  },
  // 前書面取得
  getPreContract ({ state, commit, rootState }): void {
    if (Object.keys(state.report.preContract).length) {
      return
    }
    const companyId = JWTUtils.getSubject(rootState.auth.jwt as string).companyId
    AgreementService.precontract(companyId).then(data => {
      commit('preContract', data)
    })
  },
  // 特定口座のcode取得
  getSpecificAccountCodes ({ state, commit }): void {
    if (state.specificAccountCodes) {
      return
    }
    AgreementService.specificAccountCodes().then(data => {
      commit('specificAccountCodes', data)
    })
  },
  /**
   * 同意する
   * @param {*} param0
   */
  apply ({ state, rootState }): Promise<void> {
    const userId = JWTUtils.getSubject(rootState.auth.jwt!).sub
    const postData: Partial<AgreementApplyRequest> = { userId, specificAccountCode: Number(state.other.specificAccountCode) }
    if (state.confirm.contract) {
      postData.contractId = state.report.contract.id!
    }
    if (state.confirm.preContract) {
      postData.preContractId = state.report.preContract.id!
    }
    postData.other = state.confirm.other
    return OnlineService.apply(postData).then(() => {
      return Promise.resolve()
    })
  },
  destroy ({ commit }): Promise<void> {
    commit('resetConfirm')
    return Promise.resolve()
  }
}

const mutations: MutationTree<AgreementState> = {
  contract (state, contract) {
    state.report.contract = contract
  },
  preContract (state, preContract) {
    state.report.preContract = preContract
  },
  // チェックボックス初期化
  resetConfirm (state) {
    state.confirm = { ...initialConfirm }
    state.other = {
      specificAccountCode: null,
      confirmSpecific: false
    }
    // デフォルト値
    state.other.specificAccountCode = null
  },
  confirm (state, { key, value }: { key: keyof AgreementConfirm; value: boolean }) {
    state.confirm[key] = value
  },
  specificAccountCodes (state, specificAccountCodes) {
    state.specificAccountCodes = specificAccountCodes
  },
  updateFormValue <K extends keyof AgreementOther> (state: AgreementState, { value, key }: { value: AgreementOther[K]; key: K }) {
    state.other[key] = value
  }
}

const agreement: Module<AgreementState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}

export default agreement
